import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import { useLanguage } from '../../contexts/LanguageContext';

export interface DateRangeFilterProps {
  dateFrom: string | null;
  dateTo: string | null;
  onDateFromChange: (date: string | null) => void;
  onDateToChange: (date: string | null) => void;
  minDate?: Dayjs;
}

export default function DateRangeFilter({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  minDate,
}: DateRangeFilterProps) {
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [tempStartDate, setTempStartDate] = React.useState<Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = React.useState<Dayjs | null>(null);
  const [selectingStart, setSelectingStart] = React.useState(true);
  const [calendarValue, setCalendarValue] = React.useState<Dayjs>(dayjs());

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(dateFrom ? dayjs(dateFrom) : null);
    setTempEndDate(dateTo ? dayjs(dateTo) : null);
    setSelectingStart(true);
    setCalendarValue(dateFrom ? dayjs(dateFrom) : dayjs());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;

    setCalendarValue(newValue);

    if (selectingStart) {
      setTempStartDate(newValue);
      setTempEndDate(null);
      setSelectingStart(false);
    } else {
      if (tempStartDate && newValue.isBefore(tempStartDate)) {
        setTempStartDate(newValue);
        setTempEndDate(tempStartDate);
      } else {
        setTempEndDate(newValue);
      }
    }
  };

  const handleApply = () => {
    if (tempStartDate) {
      onDateFromChange(tempStartDate.format('YYYY-MM-DD'));
    } else {
      onDateFromChange(null);
    }

    if (tempEndDate) {
      onDateToChange(tempEndDate.format('YYYY-MM-DD'));
    } else {
      onDateToChange(null);
    }

    handleClose();
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onDateFromChange(null);
    onDateToChange(null);
    handleClose();
  };

  const CustomDay = React.useCallback(
    (props: any) => {
      const { day, ...other } = props;

      if (!day) return <PickersDay {...other} day={day} />;

      const dayStr = day.format('YYYY-MM-DD');
      const startStr = tempStartDate?.format('YYYY-MM-DD');
      const endStr = tempEndDate?.format('YYYY-MM-DD');

      const isStart = startStr && dayStr === startStr;
      const isEnd = endStr && dayStr === endStr;

      let isInRange = false;
      if (tempStartDate && tempEndDate) {
        const dayTime = day.valueOf();
        const startTime = tempStartDate.valueOf();
        const endTime = tempEndDate.valueOf();
        isInRange = dayTime > startTime && dayTime < endTime;
      }

      return (
        <Box
          sx={{
            position: 'relative',
            backgroundColor: isInRange ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
            '&::before': isStart
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: isRtl ? 0 : '50%',
                  left: isRtl ? '50%' : 0,
                  bottom: 0,
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                }
              : undefined,
            '&::after': isEnd
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: isRtl ? '50%' : 0,
                  left: isRtl ? 0 : '50%',
                  bottom: 0,
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                }
              : undefined,
          }}
        >
          <PickersDay
            {...other}
            day={day}
            selected={isStart || isEnd}
            sx={{
              position: 'relative',
              zIndex: 1,
              ...(isStart || isEnd
                ? {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }
                : {}),
            }}
          />
        </Box>
      );
    },
    [tempStartDate, tempEndDate, isRtl],
  );

  const displayValue = React.useMemo(() => {
    if (dateFrom && dateTo) {
      return `${dayjs(dateFrom).format('MMM DD, YYYY')} - ${dayjs(dateTo).format('MMM DD, YYYY')}`;
    }
    if (dateFrom) {
      return `${dayjs(dateFrom).format('MMM DD, YYYY')} - ...`;
    }
    return '';
  }, [dateFrom, dateTo]);

  const placeholder = t('common.selectDateRange') || 'Select date range';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          placeholder={placeholder}
          value={displayValue}
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton size="small" edge="end" sx={{ backgroundColor: 'transparent', border: 0, '&:hover': { backgroundColor: 'transparent' } }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{
            minWidth: 280,
            cursor: 'pointer',
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              cursor: 'pointer',
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            },
            '& input': {
              cursor: 'pointer',
            },
          }}
          InputLabelProps={{
            sx: {
              textAlign: isRtl ? 'right' : 'left',
              right: isRtl ? 0 : 'auto',
              left: isRtl ? 'auto' : 0,
            },
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: isRtl ? 'right' : 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: isRtl ? 'right' : 'left',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              {selectingStart
                ? t('common.selectStartDate') || 'Select start date'
                : t('common.selectEndDate') || 'Select end date'}
            </Typography>
            <DateCalendar
              value={calendarValue}
              onChange={handleDateChange}
              minDate={minDate}
              slots={{
                day: CustomDay,
              }}
              sx={{
                '& .MuiPickersCalendarHeader-root': {
                  '& .MuiPickersArrowSwitcher-root': {
                    display: 'flex',
                    gap: 3,
                    '& .MuiPickersArrowSwitcher-button:first-of-type': {
                      order: isRtl ? 2 : 1,
                    },
                    '& .MuiPickersArrowSwitcher-button:last-of-type': {
                      order: isRtl ? 1 : 2,
                    },
                  },
                },
              }}
            />
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={handleClear}
                disabled={!tempStartDate && !tempEndDate}
                startIcon={<ClearIcon />}
              >
                {t('common.clear') || 'Clear'}
              </Button>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleClose}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApply}
                  disabled={!tempStartDate}
                >
                  {t('common.apply') || 'Apply'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}

